import React, { useEffect, useState, useCallback } from "react";
import AppLayout from "../../components/layout/AppLayout";
import { productService } from "../../services/productService";
import { Link } from "react-router-dom";
import { useNotification } from "../../context/NotificationContext";

const Inventory = () => {
  const { showNotification } = useNotification();

  // --- MEMORI (STATE): Menyimpan daftar produk dan status loading ---
  const [products, setProducts] = useState([]); // Daftar barang yang tampil di tabel
  const [loading, setLoading] = useState(true); // Status sedang mengambil data

  // --- MEMORI (STATE): Angka-angka ringkasan (Total, Nilai, Stok Rendah) ---
  const [stats, setStats] = useState({
    totalItems: 0,
    stockValue: 0,
    lowStockAlerts: 0,
    outOfStock: 0,
  });

  // Memori untuk filter: Jika benar, hanya tampilkan stok yang menipis
  const [filterLowStock, setFilterLowStock] = useState(false);

  /**
   * --- LOGIKA: Menghitung Statistik ---
   * Fungsi ini menghitung total nilai uang dari semua barang yang ada di gudang.
   */
  const calculateStats = useCallback((data) => {
    const totalItems = data.length;
    const stockValue = data.reduce(
      (acc, curr) => acc + Number(curr.price) * Number(curr.stock_level),
      0,
    );
    const lowStockAlerts = data.filter(
      (p) => p.stock_level > 0 && p.stock_level <= p.min_stock_level,
    ).length;
    const outOfStock = data.filter((p) => p.stock_level === 0).length;

    setStats({
      totalItems,
      stockValue,
      lowStockAlerts,
      outOfStock,
    });
  }, []);

  /**
   * --- LOGIKA: Mengambil Data Barang ---
   * Memanggil 'Layanan Produk' untuk mengambil semua data dari database.
   */
  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await productService.getAllProducts();

      if (data) {
        let processedData = data;
        // Jika filter aktif, buang barang yang stoknya masih banyak
        if (filterLowStock) {
          processedData = data.filter(
            (p) => p.stock_level <= p.min_stock_level,
          );
        }
        setProducts(processedData);
        calculateStats(data);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  }, [filterLowStock, calculateStats]);

  // Jalankan pengambilan data otomatis saat halaman dibuka atau filter diubah
  useEffect(() => {
    fetchProducts();
  }, [filterLowStock, fetchProducts]);

  /**
   * --- LOGIKA: Menghapus Barang ---
   * Memunculkan konfirmasi keamanan agar tidak salah hapus.
   * Menggunakan 'Soft Delete' (data tidak benar-benar hilang dari database).
   */
  const handleDelete = (id) => {
    showNotification({
      type: "confirm",
      title: "Konfirmasi Hapus",
      message: "Apakah Anda yakin ingin menghapus produk ini?",
      onConfirm: async () => {
        try {
          await productService.deleteProduct(id);

          showNotification({
            type: "success",
            title: "Berhasil",
            message: "Produk telah dihapus.",
          });
          fetchProducts(); // Segarkan daftar setelah menghapus
        } catch (error) {
          console.error("Error deleting product:", error);
          showNotification({
            type: "error",
            title: "Gagal Menghapus",
            message: error.message || "Terjadi kesalahan saat menghapus produk.",
          });
        }
      },
    });
  };

  const getCategoryIcon = (category) => {
    const cat = category?.toLowerCase() || "";
    if (cat.includes("luar")) return "public";
    if (cat.includes("dalam")) return "home";
    return "inventory_2";
  };

  /**
   * --- TAMPILAN: Label Status Stok ---
   * Fungsi ini menentukan warna label: Merah (Habis), Kuning (Menipis), Hijau (Tersedia).
   */
  const getStatusBadge = (stock, minStock) => {
    if (stock === 0) {
      return (
        <span className="inline-flex items-center whitespace-nowrap px-3 py-1.5 rounded-full bg-error-container text-on-error-container text-[10px] font-bold uppercase tracking-wider">
          Stok habis
        </span>
      );
    }
    if (stock <= minStock) {
      return (
        <span className="inline-flex items-center whitespace-nowrap px-3 py-1.5 rounded-full bg-tertiary-container text-on-tertiary text-[10px] font-bold uppercase tracking-wider">
          Stok menipis
        </span>
      );
    }
    return (
      <span className="inline-flex items-center whitespace-nowrap px-3 py-1.5 rounded-full bg-secondary-container text-on-secondary-container text-[10px] font-bold uppercase tracking-wider">
        Stok tersedia
      </span>
    );
  };

  /**
   * --- FUNGSI PEMBANTU: Format Rupiah ---
   */
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
    }).format(value);
  };

  return (
    <AppLayout pageTitle="Manajemen Inventaris">
      <div className="min-h-full space-y-6 md:space-y-10">
        {/* BAGIAN TAMPILAN: Judul & Tombol Aksi */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-6">
          <div className="space-y-1">
            <h2 className="text-2xl md:text-3xl font-extrabold font-manrope text-on-surface tracking-tight leading-tight">
              Inventaris Produk
            </h2>
            <p className="text-on-surface-variant text-xs md:text-sm max-w-md">
              Kelola dan pantau seluruh stok produk Anda dalam satu panel.
            </p>
          </div>
          <div className="flex flex-col xs:flex-row gap-3 w-full md:w-auto">
            {/* Tombol Filter Stok Rendah */}
            <button
              onClick={() => setFilterLowStock(!filterLowStock)}
              className={`flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-outline-variant/30 text-[10px] font-bold uppercase tracking-widest transition-all ${
                filterLowStock
                  ? "bg-tertiary-container text-on-tertiary-container ring-2 ring-tertiary/20"
                  : "bg-surface-container-lowest text-on-surface-variant hover:bg-surface-container-low"
              }`}
            >
              <span className="material-symbols-outlined text-lg">
                {filterLowStock ? "check_circle" : "filter_list"}
              </span>
              Stok menipis
            </button>
            {/* Tombol ke Halaman Tambah Produk */}
            <Link
              to="/inventory/add"
              className="flex-1 md:flex-none bg-gradient-to-br from-primary to-primary-container flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl text-white text-[10px] font-bold uppercase tracking-widest ambient-bloom hover:scale-[1.02] transition-all bg-primary-gradient shadow-lg shadow-primary/20"
            >
              <span className="material-symbols-outlined text-lg">add</span>
              Tambah Produk
            </Link>
          </div>
        </div>

        {/* BAGIAN TAMPILAN: Kartu Statistik Ringkasan */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <div className="bg-surface-container-lowest p-6 rounded-2xl shadow-sm border border-outline-variant/10">
            <p className="text-[10px] text-outline uppercase font-bold tracking-[0.2em] mb-1">
              Total Produk
            </p>
            <h3 className="text-2xl md:text-3xl font-manrope font-extrabold text-on-surface">
              {loading ? "..." : stats.totalItems}
            </h3>
            <div className="mt-4 flex items-center text-[10px] text-green-600 font-black uppercase tracking-tighter">
              <span className="material-symbols-outlined text-sm mr-1">
                trending_up
              </span>
              Produk Aktif
            </div>
          </div>
          <div className="bg-surface-container-lowest p-6 rounded-2xl shadow-sm border border-outline-variant/10">
            <p className="text-[10px] text-outline uppercase font-bold tracking-[0.2em] mb-1">
              Total Nilai Stok
            </p>
            <h3 className="text-2xl md:text-3xl font-manrope font-extrabold text-on-surface">
              {loading ? "..." : formatCurrency(stats.stockValue)}
            </h3>
            <div className="mt-4 flex items-center text-[10px] text-blue-600 font-black uppercase tracking-tighter">
              <span className="material-symbols-outlined text-sm mr-1">
                payments
              </span>
              Nilai Bersih
            </div>
          </div>
          <div
            className={`p-6 rounded-2xl shadow-sm border border-outline-variant/10 ${stats.lowStockAlerts > 0 ? "bg-tertiary-container/20 border-l-4 border-tertiary" : "bg-surface-container-lowest"}`}
          >
            <p className="text-[10px] text-outline uppercase font-bold tracking-[0.2em] mb-1">
              Stok menipis
            </p>
            <h3
              className={`text-2xl md:text-3xl font-manrope font-extrabold ${stats.lowStockAlerts > 0 ? "text-tertiary" : "text-on-surface"}`}
            >
              {loading ? "..." : stats.lowStockAlerts}
            </h3>
            <div
              className={`mt-4 flex items-center text-[10px] font-black uppercase tracking-tighter ${stats.lowStockAlerts > 0 ? "text-tertiary" : "text-outline"}`}
            >
              <span className="material-symbols-outlined text-sm mr-1">
                warning
              </span>
              {stats.lowStockAlerts > 0 ? "Butuh Tindakan" : "Aman"}
            </div>
          </div>
          <div className="bg-surface-container-lowest p-6 rounded-2xl shadow-sm border border-outline-variant/10">
            <p className="text-[10px] text-outline uppercase font-bold tracking-[0.2em] mb-1">
              Stok Habis
            </p>
            <h3 className="text-2xl md:text-3xl font-manrope font-extrabold text-on-surface">
              {loading ? "..." : stats.outOfStock}
            </h3>
            <div className="mt-4 flex items-center text-[10px] text-error font-black uppercase tracking-tighter">
              <span className="material-symbols-outlined text-sm mr-1">
                remove_shopping_cart
              </span>
              Stok Habis
            </div>
          </div>
        </div>

        {/* --- TABEL PRODUK UTAMA --- */}
        {/* Versi Desktop: Muncul di layar Lebar */}
        <div className="overflow-x-auto hidden md:block">
          <table className="w-full text-left border-collapse min-w-[800px]">
            <thead>
              <tr className="bg-surface-container-low/50">
                <th className="px-8 py-5 text-[11px] font-bold text-outline uppercase tracking-widest font-inter">
                  Nama Produk
                </th>
                <th className="px-8 py-5 text-[11px] font-bold text-outline uppercase tracking-widest font-inter">
                  Kategori
                </th>
                <th className="px-8 py-5 text-[11px] font-bold text-outline uppercase tracking-widest font-inter">
                  Harga
                </th>
                <th className="px-8 py-5 text-[11px] font-bold text-outline uppercase tracking-widest font-inter">
                  Stok
                </th>
                <th className="px-8 py-5 text-[11px] font-bold text-outline uppercase tracking-widest font-inter text-center">
                  Status
                </th>
                <th className="px-8 py-5 text-[11px] font-bold text-outline uppercase tracking-widest font-inter text-right">
                  Aksi
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/10">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-8 py-20 text-center">
                    <div className="flex flex-col items-center gap-4">
                      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                      <p className="text-sm text-on-surface-variant font-medium">
                        Menyiapkan data produk...
                      </p>
                    </div>
                  </td>
                </tr>
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-8 py-20 text-center">
                    <div className="flex flex-col items-center gap-4 opacity-50">
                      <span className="material-symbols-outlined text-6xl">
                        inventory
                      </span>
                      <p className="text-sm text-on-surface-variant font-medium">
                        Belum ada produk di inventaris.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                products.map((product) => (
                  <tr
                    key={product.id}
                    className="hover:bg-surface-container-low/30 transition-colors group"
                  >
                    <td className="px-8 py-6">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-primary-fixed/10 flex items-center justify-center overflow-hidden">
                          {product.image_url ? (
                            <img
                              src={product.image_url}
                              alt={product.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="material-symbols-outlined text-primary">
                              {getCategoryIcon(product.category)}
                            </span>
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-bold text-on-surface">
                            {product.name}
                          </p>
                          <p className="text-[10px] text-outline">
                            {product.sku}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-6 text-sm text-on-surface-variant">
                      {product.category}
                    </td>
                    <td className="px-8 py-6 text-sm font-semibold text-on-surface">
                      {formatCurrency(product.price)}
                    </td>
                    <td className="px-8 py-6 text-sm text-on-surface-variant">
                      {product.stock_level} unit
                    </td>
                    <td className="px-8 py-6 text-center">
                      {getStatusBadge(
                        product.stock_level,
                        product.min_stock_level,
                      )}
                    </td>
                    <td className="px-8 py-6 text-right">
                      <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Link
                          to={`/inventory/edit?id=${product.id}`}
                          className="p-2 hover:bg-primary-fixed rounded-lg text-primary transition-colors"
                        >
                          <span className="material-symbols-outlined text-lg">
                            edit
                          </span>
                        </Link>
                        <button
                          onClick={() => handleDelete(product.id)}
                          className="p-2 hover:bg-error-container rounded-lg text-error transition-colors"
                        >
                          <span className="material-symbols-outlined text-lg">
                            delete
                          </span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Versi Mobile: Muncul di layar HP (Bentuk Kartu) */}
        <div className="grid grid-cols-1 gap-4 p-4 md:hidden">
          {loading ? (
            <div className="flex flex-col items-center gap-4 py-10">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
              <p className="text-sm text-on-surface-variant font-medium">
                Menyiapkan data produk...
              </p>
            </div>
          ) : products.length === 0 ? (
            <div className="flex flex-col items-center gap-4 py-10 opacity-50">
              <span className="material-symbols-outlined text-4xl">
                inventory
              </span>
              <p className="text-sm text-on-surface-variant font-medium">
                Belum ada produk.
              </p>
            </div>
          ) : (
            products.map((product) => (
              <div
                key={product.id}
                className="bg-surface-container-low/30 border border-outline-variant/20 rounded-2xl p-4 flex flex-col gap-4"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-primary-fixed/10 flex-shrink-0 flex items-center justify-center overflow-hidden">
                      {product.image_url ? (
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="material-symbols-outlined text-primary">
                          {getCategoryIcon(product.category)}
                        </span>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-on-surface line-clamp-1">
                        {product.name}
                      </p>
                      <p className="text-[10px] text-outline tracking-wider font-mono">
                        {product.sku}
                      </p>
                    </div>
                  </div>
                  <div>
                    {getStatusBadge(
                      product.stock_level,
                      product.min_stock_level,
                    )}
                  </div>
                </div>
                <div className="flex justify-between items-center bg-surface-container-lowest p-3 rounded-xl border border-outline-variant/10">
                  <div className="flex flex-col">
                    <span className="text-[10px] font-bold text-outline uppercase tracking-widest">
                      Harga
                    </span>
                    <span className="text-sm font-bold text-on-surface">
                      {formatCurrency(product.price)}
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-[10px] font-bold text-outline uppercase tracking-widest">
                      Stok
                    </span>
                    <span className="text-sm text-on-surface-variant font-medium">
                      {product.stock_level} unit
                    </span>
                  </div>
                </div>
                {/* Tombol Aksi HP */}
                <div className="flex justify-end gap-2 border-t border-outline-variant/10 pt-3">
                  <Link
                    to={`/inventory/edit?id=${product.id}`}
                    className="flex-1 flex items-center justify-center gap-2 py-2 bg-primary-fixed hover:bg-primary-fixed-dim rounded-lg text-on-primary-fixed text-xs font-bold transition-colors"
                  >
                    <span className="material-symbols-outlined text-[16px]">
                      edit
                    </span>{" "}
                    Edit
                  </Link>
                  <button
                    onClick={() => handleDelete(product.id)}
                    className="flex-1 flex items-center justify-center gap-2 py-2 bg-error-container hover:bg-error rounded-lg text-on-error-container hover:text-white text-xs font-bold transition-all"
                  >
                    <span className="material-symbols-outlined text-[16px]">
                      delete
                    </span>{" "}
                    Hapus
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* BAGIAN TAMPILAN: Navigasi Halaman (Pagination) */}
        <div className="py-4 bg-surface-container-low/20 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-outline-variant/10">
          <p className="text-xs text-outline order-2 sm:order-1">
            Menampilkan{" "}
            <span className="text-on-surface font-bold">{products.length}</span>{" "}
            dari{" "}
            <span className="text-on-surface font-bold">
              {stats.totalItems}
            </span>{" "}
            Produk
          </p>
          <div className="flex gap-2 order-1 sm:order-2">
            <button
              disabled
              className="w-8 h-8 rounded-lg border border-outline-variant/30 flex items-center justify-center text-outline opacity-50 cursor-not-allowed"
            >
              <span className="material-symbols-outlined text-lg">
                chevron_left
              </span>
            </button>
            <button className="w-8 h-8 rounded-lg bg-primary text-white flex items-center justify-center text-xs font-bold ring-2 ring-primary/20 shadow-md shadow-primary/10">
              1
            </button>
            <button
              disabled
              className="w-8 h-8 rounded-lg border border-outline-variant/30 flex items-center justify-center text-outline opacity-50 cursor-not-allowed"
            >
              <span className="material-symbols-outlined text-lg">
                chevron_right
              </span>
            </button>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default Inventory;
